import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { RichTextCopyButton } from '@/components/ui/RichTextCopyButton'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import DOMPurify from 'dompurify'
import {
  CheckCircle,
  ChevronDown,
  Code,
  Copy,
  Download,
  ExternalLink,
  FileMinus,
  FileText,
  Info,
  RefreshCw,
  Send,
} from 'lucide-react'
import Image from 'next/image'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { seoArticleWriter } from 'sprites-types/workflow/tools/seoArticleWriter'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

// Function to safely sanitize HTML content
function sanitizeHtml(htmlContent: string): string {
  return DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'p',
      'br',
      'strong',
      'em',
      'u',
      'a',
      'ul',
      'ol',
      'li',
      'blockquote',
      'img',
      'figure',
      'figcaption',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'pre',
      'code',
      'div',
      'span',
    ],
    ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'title', 'width', 'height', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
  })
}

// Function to extract target URL for publishing
function extractTargetUrl(nodeExecution: any, metadata: any): string {
  // First try to get from inputs.targetDomain
  const targetDomain = nodeExecution.inputs?.targetDomain
  if (targetDomain && typeof targetDomain === 'string') {
    // If it already looks like a full URL, return it
    if (targetDomain.startsWith('http')) {
      return targetDomain
    }
    // Otherwise add https://
    return `https://${targetDomain}`
  }

  // Try metadata.targetDomain
  if (metadata?.targetDomain) {
    if (metadata.targetDomain.startsWith('http')) {
      return metadata.targetDomain
    }
    return `https://${metadata.targetDomain}`
  }

  // Fallback to generic site if no domain available
  return 'your site'
}

function generateArticleSummaryMarkdown(
  title: string,
  targetDomain: string | undefined,
  metaTitle: string | undefined,
  metaDescription: string | undefined,
  keywords: string[],
  wordCount: number,
  readingTime: number | undefined,
  executedAt: string,
  docxUrl?: string,
  internalLinks?: any,
  generatedImages?: any[]
): string {
  return `## 📝 SEO Article Generated

**Title:** ${title}
${targetDomain ? `**Target Domain:** ${targetDomain}` : ''}
**Generated:** ${new Date(executedAt).toLocaleString()}

### 📊 Article Stats

| Metric | Value |
|--------|-------|
| 📖 **Word Count** | ${wordCount.toLocaleString()} |
${readingTime ? `| ⏱️ **Reading Time** | ${readingTime} minutes |` : ''}
| 🎯 **Target Keywords** | ${keywords.length} |
${generatedImages && generatedImages.length > 0 ? `| 🎨 **AI Images Generated** | ${generatedImages.length} |` : ''}
${docxUrl ? `| 📄 **DOCX Available** | [Download DOCX](${docxUrl}) |` : ''}

${
  keywords.length > 0
    ? `### 🔍 SEO Keywords

${keywords
  .map((keyword, index) => {
    const emoji = index === 0 ? '🎯' : index === 1 ? '🎪' : index === 2 ? '🎨' : '⭐'
    return `${emoji} **${keyword}**`
  })
  .join('\n\n')}`
    : ''
}

${
  metaTitle || metaDescription
    ? `### 🚀 SEO Metadata

${metaTitle ? `**Meta Title:** ${metaTitle}` : ''}
${metaDescription ? `**Meta Description:** ${metaDescription}` : ''}`
    : ''
}

${
  internalLinks && internalLinks.linksAdded && internalLinks.linksAdded.length > 0
    ? `### 🔗 Internal Links Added

Added **${internalLinks.linksAdded.length} internal links** to enhance SEO and user experience:

${internalLinks.linksAdded
  .map((link: any) => {
    const priorityEmoji = link.priority === 'high' ? '🚀' : link.priority === 'medium' ? '⚡' : '🔹'
    const manualLabel = link.isManual ? ' *(manual)*' : ''
    return `${priorityEmoji} [${link.anchorText}](${link.url})${manualLabel}`
  })
  .join('\n\n')}

**Link Discovery:**
- 📊 Total URLs analyzed: ${internalLinks.totalUrlsAnalyzed || 0}
- 🗺️ Discovery method: ${internalLinks.sitemapMethod || 'sitemap'}
${internalLinks.shopifyUrlsIncluded ? `- 🛍️ Shopify URLs included: ${internalLinks.shopifyUrlsIncluded}` : ''}
${internalLinks.reasoning ? `- 💡 **Strategy:** ${internalLinks.reasoning}` : ''}`
    : ''
}

${
  generatedImages && generatedImages.length > 0
    ? `### 🎨 Generated Images

Created **${generatedImages.length} AI images** to enhance visual appeal:

${generatedImages
  .map((image: any, _index: number) => {
    return `![${image.alt}](${image.url})
*${image.placement}* | Style: ${image.style || 'Default'}
Prompt: "${image.prompt}"`
  })
  .join('\n\n')}`
    : ''
}`
}

function SeoArticleWriterDisplayComponent({
  nodeExecution,
  onOpenPanel,
}: WorkflowToolComponentProps) {
  // React hooks must be called at the top level, before any early returns
  const [isExpanded, setIsExpanded] = useState(false)
  const [isJsonLdExpanded, setIsJsonLdExpanded] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [rerollingImages, setRerollingImages] = useState<Set<number>>(new Set())
  const { sendMessage } = useCopilotContext()

  const schema = getToolResultSchema(seoArticleWriter, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  // Get result data (may be undefined if parsing failed)
  const result = parseResult.success ? parseResult.data : undefined

  // Publish handler - must be defined before early return
  const handlePublishArticle = useCallback(async () => {
    if (!parseResult.success) return

    setIsPublishing(true)
    try {
      const result = parseResult.data
      const targetUrl = extractTargetUrl(nodeExecution, result!.metadata)
      const articleTitle = result!.metadata.title || 'the article'
      const message = `I want to publish article "${articleTitle}" to ${targetUrl}`

      await sendMessage(message)
      toast.success('Publish request sent successfully!')
    } catch (error) {
      console.error('Failed to send publish message:', error)
      toast.error('Failed to send publish request')
    } finally {
      setIsPublishing(false)
    }
  }, [parseResult, nodeExecution, sendMessage])

  // Image re-roll handler
  const handleRerollImage = useCallback(
    async (imageIndex: number, originalPrompt: string, imageStyle?: string) => {
      if (!parseResult.success || !result?.generatedImages) return

      setRerollingImages((prev) => new Set(prev).add(imageIndex))

      try {
        const response = await fetch('/api/v1/seo/regenerate-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            originalPrompt,
            imageStyle,
            keywords: result!.seo.targetKeywords || [],
            articleContent: result!.content.sections
              ?.map((s) => (s as any).content)
              .join('\n\n')
              .slice(0, 1000),
          }),
        })

        const data = await response.json()

        if (data.success && data.imageUrl) {
          // Update the image URL in the result (this is a simple approach)
          // In a real application, you might want to update this through state management
          if (result!.generatedImages && result!.generatedImages[imageIndex]) {
            result!.generatedImages[imageIndex].url = data.imageUrl
            result!.generatedImages[imageIndex].prompt = data.imagePrompt || originalPrompt
          }

          toast.success('Image regenerated successfully!')

          // Trigger a re-render by updating a dummy state or use proper state management
          setRerollingImages((prev) => {
            const newSet = new Set(prev)
            newSet.delete(imageIndex)
            return newSet
          })
        } else {
          throw new Error(data.error || 'Failed to regenerate image')
        }
      } catch (error) {
        console.error('Failed to regenerate image:', error)
        toast.error('Failed to regenerate image. Please try again.')
        setRerollingImages((prev) => {
          const newSet = new Set(prev)
          newSet.delete(imageIndex)
          return newSet
        })
      }
    },
    [parseResult, result]
  )

  // Early return if parsing failed - after all hooks
  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  // Use new structured schema - result is now guaranteed to be defined
  const title = result!.metadata.title || 'Generated Article'
  const targetDomain = result!.metadata.targetDomain
  const metaTitle = result!.seo.metaTitle
  const metaDescription = result!.seo.metaDescription
  const keywords = result!.seo.targetKeywords || []
  const wordCount = result!.content.wordCount || 0
  const readingTime = result!.content.readingTime
  const executedAt = result!.metadata.publishedDate

  // Prioritize HTML output for rich content display (links, CTAs, images)
  const htmlContent = result!.outputs?.html
  const markdownContent =
    result!.articleMarkdown ||
    result!.content.sections
      ?.map((section) => {
        if (section.type === 'header') return `${'#'.repeat(section.level)} ${section.content}`
        if (section.type === 'paragraph') return section.content
        if (section.type === 'list') return section.items.map((item) => `- ${item}`).join('\n')
        return ''
      })
      .join('\n\n') ||
    ''

  const content: string = markdownContent
  const isHtmlContent = !!htmlContent && htmlContent.trim().length > 0

  const handleCopy = () => {
    // Prioritize HTML content if available for better copy experience
    const textToCopy =
      isHtmlContent && htmlContent
        ? htmlContent
        : typeof content === 'string'
          ? content
          : JSON.stringify(content)
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => toast.success('Article copied to clipboard'))
      .catch(() => toast.error('Failed to copy article'))
  }

  const handleCopyJsonLd = () => {
    if (!result!.jsonLd) return
    const jsonLdText = JSON.stringify(result!.jsonLd, null, 2)
    navigator.clipboard
      .writeText(jsonLdText)
      .then(() => toast.success('JSON-LD copied to clipboard'))
      .catch(() => toast.error('Failed to copy JSON-LD'))
  }

  const handleCopyJsonLdScript = () => {
    if (!result!.jsonLd) return
    const scriptTag = `<script type="application/ld+json">
${JSON.stringify(result!.jsonLd, null, 2)}
</script>`
    navigator.clipboard
      .writeText(scriptTag)
      .then(() => toast.success('JSON-LD script tag copied to clipboard'))
      .catch(() => toast.error('Failed to copy JSON-LD script tag'))
  }

  const handleDownloadJsonLd = () => {
    if (!result!.jsonLd) return
    const jsonLdText = JSON.stringify(result!.jsonLd, null, 2)
    const blob = new Blob([jsonLdText], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_jsonld.json`
    link.click()
    URL.revokeObjectURL(url)
    toast.success('JSON-LD file downloaded')
  }

  const handleDownload = () => {
    let textToDownload: string
    let mimeType: string
    let fileExtension: string

    if (isHtmlContent && htmlContent) {
      // Create complete HTML document for HTML content
      const completeHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    ${metaTitle ? `<meta name="title" content="${metaTitle}">` : ''}
    ${metaDescription ? `<meta name="description" content="${metaDescription}">` : ''}
    ${keywords.length > 0 ? `<meta name="keywords" content="${keywords.join(', ')}">` : ''}
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
        .intro { font-size: 1.1em; color: #666; font-style: italic; }
        .cta-container { text-align: center; margin: 30px 0; padding: 20px; }
        .cta-button { display: inline-block; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold; color: white; background-color: #007cba; }
        img { max-width: 100%; height: auto; }
    </style>
</head>
<body>
${htmlContent}
</body>
</html>`
      textToDownload = completeHtml
      mimeType = 'text/html'
      fileExtension = 'html'
    } else {
      // Fallback for non-HTML content
      textToDownload = typeof content === 'string' ? content : JSON.stringify(content, null, 2)
      mimeType = 'text/plain'
      fileExtension = 'txt'
    }

    const blob = new Blob([textToDownload], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.${fileExtension}`
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Article downloaded')
  }

  const handleDownloadDocx = () => {
    const docxUrl = result!.docxUrl || result!.file?.source?.value
    if (!docxUrl) {
      toast.error('DOCX file not available')
      return
    }

    const link = document.createElement('a')
    link.href = docxUrl
    link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.docx`
    link.target = '_blank'
    link.click()
    toast.success('DOCX downloaded')
  }

  const handleOpenInPanel = () => {
    if (!onOpenPanel) return

    const displayContent =
      isHtmlContent && htmlContent
        ? htmlContent
        : typeof content === 'string'
          ? content
          : JSON.stringify(content, null, 2)
    const fileExtension = isHtmlContent && htmlContent ? 'html' : 'md'
    const language = isHtmlContent && htmlContent ? 'html' : 'markdown'

    const panelContent = {
      type: 'file' as const,
      filePath: 'SEO Article',
      fileName: `${title}.${fileExtension}`,
      content: displayContent,
      language,
    }

    const breadcrumbs = [
      { label: 'Results', id: 'results', clickable: true },
      { label: 'SEO Article', id: 'seo-article', clickable: false },
    ]

    onOpenPanel(panelContent, breadcrumbs)
  }

  // Check if DOCX is available
  const docxUrl = result!.docxUrl || result!.file?.source?.value

  const contextActions: ContextAction[] = [
    // DOCX download first if available
    ...(docxUrl
      ? [
          {
            id: 'download-docx',
            label: 'Download DOCX',
            icon: <FileMinus className="h-4 w-4" />,
            onClick: handleDownloadDocx,
          },
        ]
      : []),
    {
      id: 'copy',
      label: 'Copy article',
      icon: <Copy className="h-4 w-4" />,
      onClick: handleCopy,
    },
    {
      id: 'download',
      label: isHtmlContent && htmlContent ? 'Download HTML' : 'Download article',
      icon: <Download className="h-4 w-4" />,
      onClick: handleDownload,
    },
    ...(result!.jsonLd
      ? [
          {
            id: 'copy-jsonld',
            label: 'Copy JSON-LD',
            icon: <Code className="h-4 w-4" />,
            onClick: handleCopyJsonLd,
          },
          {
            id: 'copy-jsonld-script',
            label: 'Copy JSON-LD Script Tag',
            icon: <Code className="h-4 w-4" />,
            onClick: handleCopyJsonLdScript,
          },
          {
            id: 'download-jsonld',
            label: 'Download JSON-LD File',
            icon: <Download className="h-4 w-4" />,
            onClick: handleDownloadJsonLd,
          },
        ]
      : []),
    ...(onOpenPanel
      ? [
          {
            id: 'open-in-panel',
            label: 'View in Detail',
            icon: <FileText className="h-4 w-4" />,
            onClick: handleOpenInPanel,
          },
        ]
      : []),
  ]

  const summaryMarkdown = generateArticleSummaryMarkdown(
    title,
    targetDomain,
    metaTitle,
    metaDescription,
    keywords,
    wordCount,
    readingTime,
    executedAt,
    docxUrl,
    result!.internalLinks,
    result!.generatedImages
  )

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <RichTextCopyButton
          markdown={content}
          html={htmlContent ?? undefined}
          buttonText="Copy as Rich Text"
          size="md"
        />
        <button
          onClick={handlePublishArticle}
          disabled={isPublishing}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className={`h-4 w-4 ${isPublishing ? 'animate-pulse' : ''}`} />
          {isPublishing ? 'Publishing...' : 'Publish article'}
        </button>
      </div>

      <ContextMenuWrapper actions={contextActions}>
        <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
          <div className="px-4 py-3">
            <EnhancedMarkdown
              className="prose prose-sm max-w-none"
              style={{ backgroundColor: 'transparent', color: '#374151' }}
            >
              {summaryMarkdown}
            </EnhancedMarkdown>

            <details className="mt-4 w-full" open={isExpanded}>
              <summary
                className="flex w-full cursor-pointer list-none items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                onClick={(e) => {
                  e.preventDefault()
                  setIsExpanded(!isExpanded)
                }}
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                />
                <span>📄 Full Article Content</span>
              </summary>
              {isExpanded && (
                <div className="mt-3">
                  <div className="rounded-2xl border border-gray-200/50 bg-white/70 p-4">
                    {isHtmlContent && htmlContent ? (
                      // Render HTML directly to preserve links, CTAs, and images
                      <div
                        className="prose prose-sm max-w-none"
                        style={{ backgroundColor: 'transparent', color: '#374151' }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }}
                      />
                    ) : typeof content === 'string' ? (
                      // Fallback to markdown rendering
                      <EnhancedMarkdown
                        className="prose prose-sm max-w-none"
                        style={{ backgroundColor: 'transparent', color: '#374151' }}
                      >
                        {content}
                      </EnhancedMarkdown>
                    ) : (
                      // Fallback for non-string content
                      <pre className="whitespace-pre-wrap text-xs text-gray-700">
                        {JSON.stringify(content, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </details>

            {/* Generated Images Section */}
            {result!.generatedImages && result!.generatedImages.length > 0 && (
              <div className="mt-4 w-full">
                <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-purple-700">
                    <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                    🎨 Generated Images ({result!.generatedImages.length})
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {result!.generatedImages.map((image: any, index: number) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-gray-200/50 bg-white/70 p-4"
                    >
                      <div className="space-y-3">
                        <div className="relative">
                          <Image
                            src={image.url}
                            alt={image.alt}
                            width={400}
                            height={192}
                            className="h-48 w-full rounded-lg object-cover"
                          />
                          <div className="absolute right-2 top-2">
                            <button
                              onClick={() => handleRerollImage(index, image.prompt, image.style)}
                              disabled={rerollingImages.has(index)}
                              className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-1 text-xs font-medium text-purple-700 shadow-sm backdrop-blur-sm transition-colors hover:bg-white disabled:opacity-50"
                            >
                              <RefreshCw
                                className={`h-3 w-3 ${rerollingImages.has(index) ? 'animate-spin' : ''}`}
                              />
                              {rerollingImages.has(index) ? 'Generating...' : 'Re-roll'}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2 text-xs">
                          <div>
                            <span className="font-medium text-gray-700">Placement:</span>
                            <span className="ml-1 text-gray-600">{image.placement}</span>
                          </div>
                          {image.style && (
                            <div>
                              <span className="font-medium text-gray-700">Style:</span>
                              <span className="ml-1 text-gray-600">{image.style}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-gray-700">Prompt:</span>
                            <span className="ml-1 text-gray-600">{image.prompt}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* JSON-LD Structured Data Section */}
            {result!.jsonLd && (
              <details className="mt-4 w-full" open={isJsonLdExpanded}>
                <summary
                  className="flex w-full cursor-pointer list-none items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-100"
                  onClick={(e) => {
                    e.preventDefault()
                    setIsJsonLdExpanded(!isJsonLdExpanded)
                  }}
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${isJsonLdExpanded ? 'rotate-90' : ''}`}
                  />
                  <Code className="h-4 w-4 text-green-600" />
                  <CheckCircle className="h-3 w-3 text-green-600" />
                  <span>🔍 JSON-LD Structured Data</span>
                  <span className="ml-auto flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    <span className="text-xs text-green-600">Schema.org</span>
                  </span>
                </summary>
                {isJsonLdExpanded && (
                  <div className="mt-3">
                    <div className="rounded-2xl border border-green-200/50 bg-white/70 p-4">
                      {/* Educational Header */}
                      <div className="mb-4 rounded-lg bg-green-50 p-3">
                        <div className="flex items-start gap-2">
                          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                          <div className="text-sm">
                            <p className="mb-1 font-medium text-green-800">SEO Benefits</p>
                            <p className="text-xs leading-relaxed text-green-700">
                              This Schema.org structured data helps search engines understand your
                              content better, potentially improving rich snippets, search rankings,
                              and click-through rates.
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                                <ExternalLink className="h-2.5 w-2.5" />
                                Google
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                                <ExternalLink className="h-2.5 w-2.5" />
                                Bing
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                                <ExternalLink className="h-2.5 w-2.5" />
                                Yandex
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* JSON-LD Preview */}
                      <div className="mb-3">
                        <h4 className="mb-2 text-sm font-medium text-gray-800">
                          Key Structured Data Fields
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div className="rounded-lg bg-gray-50 p-2">
                            <span className="font-medium text-gray-700">Type:</span>
                            <span className="ml-1 text-gray-600">BlogPosting</span>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-2">
                            <span className="font-medium text-gray-700">Headline:</span>
                            <span className="ml-1 block truncate text-gray-600">
                              {result!.jsonLd.headline}
                            </span>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-2">
                            <span className="font-medium text-gray-700">Author:</span>
                            <span className="ml-1 text-gray-600">{result!.jsonLd.author.name}</span>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-2">
                            <span className="font-medium text-gray-700">Publisher:</span>
                            <span className="ml-1 text-gray-600">
                              {result!.jsonLd.publisher.name}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mb-3 flex flex-wrap gap-2">
                        <button
                          onClick={handleCopyJsonLd}
                          className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-200"
                        >
                          <Copy className="h-3 w-3" />
                          Copy JSON
                        </button>
                        <button
                          onClick={handleCopyJsonLdScript}
                          className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-200"
                        >
                          <Code className="h-3 w-3" />
                          Copy HTML Script
                        </button>
                        <button
                          onClick={handleDownloadJsonLd}
                          className="inline-flex items-center gap-1 rounded-md bg-green-100 px-2 py-1 text-xs text-green-700 transition-colors hover:bg-green-200"
                        >
                          <Download className="h-3 w-3" />
                          Download .json
                        </button>
                      </div>

                      {/* Full JSON-LD Code */}
                      <div className="overflow-x-auto rounded-lg bg-gray-900 p-3">
                        <pre className="whitespace-pre-wrap text-xs text-gray-100">
                          <code className="language-json">
                            {JSON.stringify(result!.jsonLd, null, 2)}
                          </code>
                        </pre>
                      </div>

                      {/* Usage Instructions */}
                      <div className="mt-3 rounded-lg bg-blue-50 p-3">
                        <p className="mb-1 text-xs font-medium text-blue-800">How to Use:</p>
                        <ol className="list-decimal space-y-1 pl-4 text-xs text-blue-700">
                          <li>Copy the HTML Script tag above</li>
                          <li>Add it to your webpage&apos;s &lt;head&gt; section</li>
                          <li>Test with Google&apos;s Rich Results Test tool</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                )}
              </details>
            )}
          </div>
        </div>
      </ContextMenuWrapper>
    </div>
  )
}

function downloadSeoArticleWriter(nodeExecution: NodeExecution): Promise<void> {
  const schema = getToolResultSchema(seoArticleWriter, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    console.error('Schema validation failed for download:', parseResult.error)
    return Promise.resolve()
  }

  const result = parseResult.data

  // Use new structured schema for download - prioritize HTML
  const title = result!.metadata.title || 'Generated Article'
  const htmlContent = result!.outputs?.html
  const isHtmlContent = !!htmlContent && htmlContent.trim().length > 0

  const markdownContent =
    result!.articleMarkdown ||
    result!.content.sections
      ?.map((section) => {
        if (section.type === 'header') return `${'#'.repeat(section.level)} ${section.content}`
        if (section.type === 'paragraph') return section.content
        if (section.type === 'list') return section.items.map((item) => `- ${item}`).join('\n')
        return ''
      })
      .join('\n\n') ||
    ''

  let downloadContent: string
  let mimeType: string
  let fileExtension: string

  if (isHtmlContent && htmlContent) {
    // Create complete HTML document for HTML content
    const metaTitle = result!.seo.metaTitle
    const metaDescription = result!.seo.metaDescription
    const keywords = result!.seo.targetKeywords || []

    downloadContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    ${metaTitle ? `<meta name="title" content="${metaTitle}">` : ''}
    ${metaDescription ? `<meta name="description" content="${metaDescription}">` : ''}
    ${keywords.length > 0 ? `<meta name="keywords" content="${keywords.join(', ')}">` : ''}
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
        .intro { font-size: 1.1em; color: #666; font-style: italic; }
        .cta-container { text-align: center; margin: 30px 0; padding: 20px; }
        .cta-button { display: inline-block; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold; color: white; background-color: #007cba; }
        img { max-width: 100%; height: auto; }
    </style>
</head>
<body>
${htmlContent}
</body>
</html>`
    mimeType = 'text/html'
    fileExtension = 'html'
  } else {
    // Fallback for non-HTML content
    downloadContent =
      typeof markdownContent === 'string'
        ? markdownContent
        : JSON.stringify(markdownContent, null, 2)

    // Include JSON-LD if available for non-HTML content
    if (result!.jsonLd) {
      downloadContent += '\n\n---\n\n## JSON-LD Structured Data\n\n'
      downloadContent += '```json\n'
      downloadContent += JSON.stringify(result!.jsonLd, null, 2)
      downloadContent += '\n```\n'
    }

    mimeType = 'text/plain'
    fileExtension = 'txt'
  }

  const blob = new Blob([downloadContent], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.${fileExtension}`
  link.click()
  URL.revokeObjectURL(url)

  return Promise.resolve()
}

function seoArticleWriterToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(seoArticleWriter, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  // Use new structured schema for markdown
  const title = result!.metadata.title || 'Generated Article'
  const targetDomain = result!.metadata.targetDomain
  const keywords = result!.seo.targetKeywords || []
  const metaTitle = result!.seo.metaTitle
  const metaDescription = result!.seo.metaDescription
  const wordCount = result!.content.wordCount || 0
  const readingTime = result!.content.readingTime
  const executedAt = result!.metadata.publishedDate

  // Prioritize HTML content if available for export, fallback to markdown
  const htmlContent = result!.outputs?.html
  const isHtmlContent = !!htmlContent && htmlContent.trim().length > 0

  const markdownContent =
    result!.articleMarkdown ||
    result!.content.sections
      ?.map((section) => {
        if (section.type === 'header') return `${'#'.repeat(section.level)} ${section.content}`
        if (section.type === 'paragraph') return section.content
        if (section.type === 'list') return section.items.map((item) => `- ${item}`).join('\n')
        return ''
      })
      .join('\n\n') ||
    ''

  const content = isHtmlContent && htmlContent ? htmlContent : markdownContent

  // Generate the same summary as the UI
  const docxUrl = result!.docxUrl || result!.file?.source?.value
  const summaryMarkdown = generateArticleSummaryMarkdown(
    title,
    targetDomain,
    metaTitle,
    metaDescription,
    keywords,
    wordCount,
    readingTime,
    executedAt,
    docxUrl,
    result!.internalLinks,
    result!.generatedImages
  )

  let markdown = summaryMarkdown + '\n\n## 📄 Full Article\n\n'
  markdown += typeof content === 'string' ? content : JSON.stringify(content, null, 2)

  // Include JSON-LD if available
  if (result!.jsonLd) {
    markdown += '\n\n## 🔍 JSON-LD Structured Data\n\n'
    markdown += '```json\n'
    markdown += JSON.stringify(result!.jsonLd, null, 2)
    markdown += '\n```\n'
  }

  markdown += '\n\n---\n'

  return markdown
}

export const SeoArticleWriterDisplay = createWorkflowToolComponent(
  SeoArticleWriterDisplayComponent,
  downloadSeoArticleWriter,
  seoArticleWriterToMarkdown
)
